import {
	Alert,
	Box,
	Button,
} from '@mui/material';
import Text from '../text';
import PrimaryButton from '../button/PrimaryButton';
import SecondaryButton from '../button/SecondaryButton';
import ApiService from 'utils/services/ApiService';
import { usePopupMessage } from '../../hooks/SendPopUpMessage';
import React, { useState, ChangeEvent, useEffect, useRef } from 'react';
import {
    Typography,
    IconButton,
    Divider,
} from '@mui/material';
import { FileUpload } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
	sendAnalyzerCommand,
    sendXMODEM,
	responseEmitter
} from '../../utils/usb/USBConnector';
import { setHeartbeat } from 'utils/redux/reducers/heartbeatReducer';
import { useDispatch, useSelector } from 'react-redux';
import FirmwareUpdateFailureToast from '../../components/toast/firmwareUpdateFailure';
import { toast } from 'react-toastify';
import { BRANDS, ORG_NAME_TO_BRANDS } from 'utils/constants/brands';
import { IReduxState } from 'utils/models/redux-states';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import FirmwareSpinner from '../dialog/FirmwareSpinner';
import ImportModal from '../import-modal';
import { connected } from 'process';
interface DeviceWarrantyProps {
	mode: 'online' | 'offline';
	userDetails: any;
	// onUpdateUserDetails: (userDetails: any) => void;
	isConnected: boolean;
	isAnalyzerConnected: boolean;
	handleConnectToPort: () => void;
}
type RegisteredDevice = {
    	serialValue: string;
}
const DEVELOPMENT_FIRMWARE_CHANGE_READY = true;
const DeviceWarranty: React.FC<DeviceWarrantyProps> = ({
	mode,
	userDetails,
	// onUpdateUserDetails,
	isConnected,
	isAnalyzerConnected,
	handleConnectToPort
}) => {
	const [currentVersion, setCurrentVersion] = useState<string>('');
	const [deviceBrandFlavour, setDeviceBrandFlavour] = useState<string>('');
	const [latestServerFlavour, setLatestServerFlavour] = useState<string>('');
	const [latestVersion, setLatestVersion] = useState<string>('');
	const [isUpToDate, setIsUpToDate] = useState<boolean>(true);
	const [isUpToDateLabelOn, setIsUpToDateLabelOn] = useState<boolean>(false);
	const [registeredDevices, setRegisteredDevices] = useState<RegisteredDevice[]>([]);
	const [firmwareLink, setFirmwareLink] = useState<string>('');
	const [percent, setPercent] = useState<number>(0);
	const [isBackedUp, setIsBackedUp] = useState<boolean>(false);
	const [testsStored, setTestsStored] = useState<number>(-1);
	const [testsImported, setTestsImported] = useState<number>(0);
	const [loadingNumberOfTests, setLoadingNumberOfTests] = useState<boolean>(true);
	const [isImportOpen, setIsImportOpen] = useState(false);
	const reduxState = useSelector((state: IReduxState) => state.user);
	const userData = reduxState?.userInformation;

	// Called during initial render
	// Get latest version, and auto update setting
	useEffect(() => {
		getAnalyzerWarranties();
		getLatestFWInfo();
	}, []);

	useEffect(() => {
		getLatestFWInfo();
	}, [isConnected]);

	const getAnalyzerWarranties = async () => {
		if (userData?.id && navigator.onLine) {
			const results = await ApiService.getAnalyzerWarranties(userData.id);
			setRegisteredDevices(results.data);
		}
	}

	function openImportModal () {
		setIsImportOpen(true);
	}

	function closeImportModal (importedResults?: number) {
		if (importedResults !== undefined) {
			setTestsImported(importedResults);
			setIsBackedUp(true);
		}
		setIsImportOpen(false);
	}

	async function getLatestFWInfo() {

		await new Promise(resolve => setTimeout(resolve, 1000));

		let analyzerBrandId = Number(localStorage.getItem('analyzerBrandId'));

		if (!analyzerBrandId) {
			return;
		}

		ApiService.getLatestAppVersionBCA(analyzerBrandId ?? 1)
			.then((response) => {
				let data = response.data;

				//sort the data with the highest appVersion first
				data.sort((a: any, b: any) => {
					if (a.appVersion < b.appVersion) return 1;
					if (a.appVersion > b.appVersion) return -1;
					return 0;
				})

				const latestInfo = data[0];

				const deviceVersion = determineCurrentVersion();
				const deviceFlavour = determineFlavorString();

				if (latestInfo) {

					setLatestVersion(latestInfo.appVersion);
					setLatestServerFlavour(ORG_NAME_TO_BRANDS[latestInfo.applyToOrg]);
					setIsUpToDate(deviceVersion === latestInfo.appVersion && checkIfOrgNamesMatch(deviceFlavour, latestInfo.applyToOrg));
					setIsUpToDateLabelOn(true);
					setFirmwareLink(latestInfo.downloadURL);

				}

			}).catch((error) => {
				console.error('Error fetching latest firmware info:', error);
			});

	}

	function determineFlavorString(newId?: string) {

		const brandId = newId ?? localStorage.getItem('analyzerBrandId');
		let brandName;

		brandName = BRANDS[Number(brandId)];

		setDeviceBrandFlavour(brandName);
		return brandName;

	}

	function determineCurrentVersion(newVersion?: string) {

		const version = newVersion ?? localStorage.getItem('analyzerFirmwareVersion');

		setCurrentVersion(version ?? '');
		return version ?? '';

	}

	function checkIfOrgNamesMatch(name1: string, name2: string): boolean {

		//This is only for Analyzer since the names are not consistent
		if (name1 === name2) {
			return true;
		}
		if (name1 === "Luminultra" && name2 === "LuminUltra") {
			return true;
		}
		if (name1 === "Accucount" && (name2 === "Nalco Champion" || name2 === "ChampionX")) {
			return true;
		}
		if (name1 === "Lumikem" && name2 === "Kemira") {
			return true;
		}
		if (name1 === "Biohealth" && (name2 === "Suez" || name2 === "Veolia")) {
			return true;
		}

		return false;

	}

	// 3 Cases of serialNumber:
	// serialValue: { "serialNum": ...} or { "serialString": ... }
	// or just the string value.
	const parseSerialValue = (raw: string): string | undefined => {
		try {
			const parsed = JSON.parse(raw);
			if (typeof parsed === 'object' && parsed !== null) {
				return parsed.serialNum ?? parsed.serialString ?? undefined;
			}
			if (typeof parsed === 'string') {
				return parsed;
			}
		} catch {
			return raw;
		}
		return undefined;
	};

	const sendPopUpMessage = usePopupMessage();
	const dispatch = useDispatch();
	const serialNumber = localStorage.getItem('analyzerSerialNumber') ?? 'N/A';
	const isPBM = localStorage.getItem('connectedDeviceType') === 'PBM';
	const [isSpinnerOpen, setIsSpinnerOpen] = useState(false);
	const closeSpinner = () => setIsSpinnerOpen(false);
	const openSpinner = () => setIsSpinnerOpen(true);
	
	const [firmwareFile, setFirmwareFile] = useState<File | null>(null);
	const clearFile = () => setFirmwareFile(null);

	async function saveAnalyzerToAccount() {

		const data = {
			serialValue: serialNumber
		}

		const response = await ApiService.setAnalyzerWarranty(data);

		if (response?.data === "error") {
			sendPopUpMessage('Error', 'This device is already registered.');
		} else if (response?.data === "success") {
			sendPopUpMessage('Success', 'Device registered successfully.');
		} else {
			sendPopUpMessage('Error', 'An unexpected error occurred. Please try again later.');
		}

		getAnalyzerWarranties();

	}

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0 && !e.target.files[0].name.endsWith('.xfw')) {
			sendPopUpMessage('Error', 'Invalid firmware file type.');
			return;
		}
		if (e.target.files && e.target.files.length > 0)
			setFirmwareFile(e.target.files[0]);
		else
			setFirmwareFile(null);
		e.target.value = "";
	};

	let last = -1;
	const progressUpdate = (percent: number) => {
		if (percent !== last) {
			last = percent;
			setPercent(percent);
		}
	}

	const installBootloader = async (file?: File) => {
        dispatch(setHeartbeat(false));
        openSpinner();

		const fileToUse = (file && file instanceof File) ? file : firmwareFile;
		const existingVersion = determineCurrentVersion();

        try {
			if (!isAnalyzerConnected) {
				throw new Error('Analyzer not connected.');
			}
			if (fileToUse === null) {
				throw new Error('No firmware file available.');
			}

            await sendAnalyzerCommand("getDeviceInfo\n");
            await new Promise(resolve => setTimeout(resolve, 1000));

            await sendAnalyzerCommand("setBootloaderMode\n");
            await new Promise(resolve => setTimeout(resolve, 3000));

            sendAnalyzerCommand("getDeviceInfo\n");
            await new Promise(resolve => setTimeout(resolve, 1000));

            sendAnalyzerCommand("startUpdate\n");
            await new Promise(resolve => setTimeout(resolve, 4000));

            //XMODEM file transfer will start here, first waiting for a standalone 'C' character
            await sendXMODEM(fileToUse, progressUpdate);
            await new Promise(resolve => setTimeout(resolve, 2000));

            sendAnalyzerCommand("jumpToApp\n");
            await new Promise(resolve => setTimeout(resolve, 3000));

			responseEmitter.on('data', handleGetDeviceInfo);
			sendAnalyzerCommand("getDeviceInfo\n");

			await new Promise(resolve => setTimeout(resolve, 2000));
            responseEmitter.removeListener('data', handleGetDeviceInfo);

            sendPopUpMessage('Success', `Firmware updated to v${latestVersion}.`);
			void addEventLog('Firmware Update Success', 'Firmware Update', undefined, undefined, `serialId: ${serialNumber}, existing firmware Version: ${existingVersion}, new version: ${latestVersion}`, );

		} catch (error) {
			void addEventLog('Firmware Update Failed',
				 'Firmware Update',
				  undefined, undefined,
				   `serialId: ${serialNumber}, existing firmware Version: ${existingVersion}, new version: ${fileToUse instanceof File ? fileToUse?.name : "Auto Update"}, Error: ${error}`, );
            toast(<FirmwareUpdateFailureToast />, {
				position: 'bottom-left',
				autoClose: 8000,
				hideProgressBar: true,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				className: 'toast-background-color-usb',
			});

			console.error('fw update fail', error);

            //Attempt to bring the device out of the bootloader
            await new Promise(resolve => setTimeout(resolve, 2000));
            sendAnalyzerCommand("jumpToApp\n");
            await new Promise(resolve => setTimeout(resolve, 3000));
            sendAnalyzerCommand("getDeviceInfo\n");

        } finally {

            dispatch(setHeartbeat(true));
			await getLatestFWInfo();
            closeSpinner();
			setPercent(0);

        }

	}

	function handleGetDeviceInfo(response: any) {

		const trimmedResponse =
			typeof response === 'string'
				? response.trim()
				: String(response).trim();

		if (trimmedResponse.includes(',')) {

			const parts = trimmedResponse.split(',');
			//format: <serial number>,<firmware version>,<hardware version>,<test kit flavor>
			const [serialNumber, firmwareVersion, hardwareVersion, brandId] = parts;
			let trimmedSerial = serialNumber;
			if (serialNumber.includes('04BA6')) {
				trimmedSerial = serialNumber.substring(5);
			}
			localStorage.setItem('analyzerSerialNumber', trimmedSerial);
			localStorage.setItem('analyzerFirmwareVersion', firmwareVersion);
			localStorage.setItem('analyzerHardwareVersion', hardwareVersion);
			localStorage.setItem('analyzerBrandId', brandId);

			const deviceFlavour = determineFlavorString(brandId);
			const deviceVersion = determineCurrentVersion(firmwareVersion);

			setIsUpToDate(deviceVersion === latestVersion && checkIfOrgNamesMatch(deviceFlavour, latestServerFlavour));
			setIsUpToDateLabelOn(true);

		}

	}

	async function downloadLatestFirmware() {

		if (firmwareLink && firmwareLink !== '') {

			//attempt to download the file at the specified link from the DB.
			const response = await fetch(firmwareLink);
			const blob = await response.blob();

			const file = new File([blob], 'latestFirmware.xfw', { type: blob.type });

			return file;

		}
		return null;

	}

	async function downloadAndInstallFW() {

		try {

			openSpinner();

			const file = await downloadLatestFirmware();

			await new Promise(resolve => setTimeout(resolve, 2000));

			if (file) {
				await installBootloader(file);
			}

		} catch (err) {
			console.error("Error auto-updating firmware:", err);
		} finally {
			closeSpinner();
		}

	}

	const [fullData, setFullData] = useState<string[]>([]);
	useEffect(() => {
		let numRealTests = 0;

		for (let i = 0; i < fullData.length;) {

			if (fullData[i].includes("RLU only")) {
				i+=4;
				continue;
			} else if (fullData[i].startsWith('RESULTS') && i+1 < fullData.length) {
				numRealTests++;
			}

			i++;
		}

		setTestsStored(numRealTests);
		if (numRealTests === 0) {
			setIsBackedUp(true);
		} else {
			setIsBackedUp(false);
		}

	}, [fullData]);

	let tempFullData: string[] = [];
	let targetNumberOfTests = 0;
	let numberOfTests = 0;
	let lastExpectedLines = -1;
	function handleAnalyzerData(response: any) {

		if (response.startsWith('N saved test')) {
			targetNumberOfTests = Number.parseInt(response.substring(14));
			tempFullData = [];
			if (targetNumberOfTests === 0) {
				setFullData([]);
				responseEmitter.removeListener('data', handleAnalyzerData);
			}
			return;
		}

		if (
			tempFullData.at(-1) !== response &&
			!response.includes("getDeviceInfo") &&
			!response.includes("getAllStored") &&
			!response.includes("bad command") &&
			!(serialNumber !== '' && response.includes(serialNumber))
		) {
			tempFullData = [...tempFullData, response];
			let fullDataReceived = determineFullDataStoppingPoint(response);

			if (fullDataReceived) { 
				setFullData(tempFullData);
				responseEmitter.removeListener('data', handleAnalyzerData);
				setLoadingNumberOfTests(false);
			}

		}

	}

	function determineFullDataStoppingPoint(response: string): boolean {
	
		//CORRUPT responses count towards the total
		if (response.includes(',KIT:') || response.includes('CORRUPT ')) {
			numberOfTests++;
		}

		if (lastExpectedLines > -1) {
			lastExpectedLines--;
		}

		if (numberOfTests === targetNumberOfTests && lastExpectedLines === -1) {
			lastExpectedLines = response.includes('RLU only') ? 3 : 4;
			if (response.includes('CORRUPT ')) {
				lastExpectedLines = 0;
			}
		}

		if (lastExpectedLines === 0) { 
			lastExpectedLines = -1;
			numberOfTests = 0;
			return true;
		}

		return false;

	}

	let getAllCommandSent = false;
	useEffect(() => {
		if (!getAllCommandSent) {
			setIsBackedUp(false);
			getAllCommandSent = true;
			responseEmitter.on('data', handleAnalyzerData);
			void sendAnalyzerCommand('getAllStoredTests\n');
		}
	}, [isAnalyzerConnected])

	const isOffline = mode === 'offline';
	return (
		
		<div id="analyzer-warranty">
			{mode === 'offline' && (
				<div
					style={{
						width: '736px',
						border: '1px solid #FFA726',
						backgroundColor: '#FFF4E5',
						padding: '8px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						marginBottom: '10px',
						marginTop: '10px',
						marginRight: '20px',
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<img
							src="/assets/icons/Wifi-orange.svg"
							alt="Offline"
							style={{ marginRight: '10px' }}
						/>
						<Typography
							variant="body1"
							color="#66491F"
							className="!font-jost"
						>
							You're currently offline. Please reconnect to make
							changes.
						</Typography>
					</div>
					<IconButton sx={{ p: 0, mr: '5px', mt: '2px' }}>
						<img
							src="/assets/icons/Close.svg"
							alt="Remove"
							className="w-5 h-5 md:block hidden"
						/>
					</IconButton>
				</div>
			)}
			{/* Register a Device Section */}
			<div>
				<Text addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mt-[24px] !font-semibold !mb-[10px]">
					Warranty Registration
				</Text>
				<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] ">
					Register your device to support warranty coverage and faster support. Connect your Bugcount analyzer to get started.
				</Text>
				<div style={{ display: 'flex', marginTop: 20 }}>
					{}
				</div>
				<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] ">
					<b>Instructions:</b>
					<div style={{ paddingLeft: 20 }}>
						<li>Plug your device into your computer via USB, then select "Connect".</li>
						<li>Save the device to your account to finalize your warranty.</li>
					</div>
				</Text>

				<div style={{ display: 'flex', marginTop: 20 }}>
					{}
				</div>

				<div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
					<Button onClick={handleConnectToPort} style={{ textTransform: 'none' }}>
						<img
							src={`/assets/icons/${isConnected ? 'Usb.svg' : 'offline.svg'
								}`}
							alt={`${isConnected ? 'Connected' : 'Offline'
								} Icon`}
							className="w-5 h-5 md:block hidden mr-2"
						/>
						<span className="md:block hidden !font-jost !text-[#2C2C2C] !text-[16px]">
							{isConnected ? 'Connected' : 'Connect'}
						</span>
					</Button>
				</div>

				<div style={{ display: 'flex', marginTop: 20 }}>
					{}
				</div>

				<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] ">
					Serial Number: {serialNumber}
				</Text>

				<div style={{ display: 'flex', marginTop: 20 }}>
					{}
				</div>

				<div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
					<PrimaryButton 
						onClick={saveAnalyzerToAccount}
						disabled={serialNumber === 'N/A' || !isConnected || !isAnalyzerConnected || !navigator.onLine}
					>
						<span className="md:block hidden !font-inter !text-[14px]">
							Save to Account
						</span>
					</PrimaryButton>
				</div>
			</div>

			{/* Registered Devices Section */}
			<div>
				<Divider variant="middle" style={{ margin: '24px 0' }} />
				<Text addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mb-[10px] !font-semibold">
					Registered Devices
				</Text>

				{registeredDevices.length === 0 ? (
					<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px]">
						No devices registered.
					</Text>
				) : (
					registeredDevices.map((device, index) => {
						const serial = parseSerialValue(device.serialValue);
						return (
							<div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
								<img
									src="/assets/icons/analyzer_icon.png"
									alt="Device Icon"
									className="w-5 h-5 md:block hidden"
								/>
								<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px]">
									S/N: {serial ?? <span style={{ color: '#888' }}>Serial not set</span>}
								</Text>
							</div>
						);
					})
				)}
			</div>

			{/*Firmware Update Section*/}
			{DEVELOPMENT_FIRMWARE_CHANGE_READY &&
			<div>
				<Divider variant="middle" style={{ margin: '24px 0' }} />
				<Text addStyle="!text-[#2C2C2C] !font-inter !text-[20px] !mb-[20px] !font-semibold">
					Firmware Updates
				</Text>
				<div style={{ display: 'flex', gap: '2px', alignItems: 'center', paddingBottom: '10px', marginTop: '20px' }}>
					<Button onClick={handleConnectToPort} style={{ textTransform: 'none' }}>
						<img
							src={`/assets/icons/${isConnected ? 'Usb.svg' : 'offline.svg'
								}`}
							alt={`${isConnected ? 'Connected' : 'Offline'
								} Icon`}
							className="w-5 h-5 md:block hidden mr-2"
						/>
						<span className="md:block hidden !font-jost !text-[#2C2C2C] !text-[16px]">
							{isConnected ? 'Connected' : 'Connect'}
						</span>
					</Button>
				</div>
				<div>
					
					<div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
						<Text addStyle="!text-[#2C2C2C] !font-inter !text-[16px] !font-semibold !w-[170px]">
							Firmware on device: 
						</Text>
						<Text> {deviceBrandFlavour}{currentVersion === '' ? "" : " v"}{currentVersion} </Text>
					</div>
					<div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
						<Text addStyle="!text-[#2C2C2C] !font-inter !text-[16px] !font-semibold !w-[170px]">
							Latest version: 
						</Text>
						<Text> {latestServerFlavour}{latestVersion === '' ? "" : " v"}{latestVersion} </Text>
					</div>
					{isUpToDate && isUpToDateLabelOn && (
						<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[10px]">
							This device is running the latest firmware.
						</Text>
					)}

					{!isUpToDate && isUpToDateLabelOn && testsStored > 0 && !isBackedUp && (
						<Alert
							sx={{
								border: 1,
								padding: "6px 14px",
								mt: 2,
								fontFamily: 'jost',
								fontSize: '16px',
								maxWidth: '430px',
								color: '#2C2C2C',
								position: "static !important",
							}}
							severity="info"
						>
							<Box display="flex" alignItems="center" fontFamily="jost">
								<Typography component="div" sx={{ }} mr={3}>
									<Text addStyle="!text-[#2C2C2C] !font-bold !text-[16px]">
										Tip: Back up your test results before updating
									</Text>
									<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[2px] !mb-[10px]">
										This analyzer has {testsStored === 1 ? "1 test stored" : testsStored + " tests stored"}. Importing to Relay provides a secure backup of your data.
									</Text>

									<PrimaryButton onClick={openImportModal}>
										IMPORT RESULTS
									</PrimaryButton>
									
								</Typography>
							</Box>

						</Alert>
					)}

					{testsImported > 0 && isBackedUp && (
						<Alert
							sx={{
								border: 1,
								padding: "6px 14px",
								mt: 2,
								fontFamily: 'jost',
								fontSize: '16px',
								maxWidth: '430px',
								color: '#2C2C2C',
								position: "static !important",
							}}
							severity="success"
						>
							<Box display="flex" alignItems="center" fontFamily="jost">
								<Typography component="div" sx={{ }} mr={3}>
									<Text addStyle="!text-[#2C2C2C] !font-bold !text-[16px]">
										Your test results are backed up
									</Text>
									<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[2px] !mb-[4px]">
										{testsImported === 1 ? "1 test" : testsImported + " tests"} imported to Relay. You're ready to update.
									</Text>
								</Typography>
							</Box>

						</Alert>
					)}

					<div style={{ marginTop: '20px' }}>
						<PrimaryButton secondary={!isBackedUp} onClick={downloadAndInstallFW} disabled={isConnected === false || isAnalyzerConnected === false || !navigator.onLine || isUpToDate === true}>
							AUTO UPDATE
						</PrimaryButton>
					</div>
					{!(loadingNumberOfTests || isConnected === false || isAnalyzerConnected === false || !navigator.onLine || isUpToDate === true) && (
					<div>
						<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[10px]">
							The update may take up to 10 minutes. Keep the analyzer connected until it finishes.
						</Text>
					</div>
					)}
				</div>



				{!true && isUpToDate && (
				<div>
					<Text addStyle="!text-[#2C2C2C] !font-jost !text-[16px] !mt-[20px] !font-semibold">
						Manual update:
					</Text>
					<div style={{paddingTop: '10px'}} >
						<PrimaryButton 
							component="label" 
							startIcon={<FileUpload/>} 
							style={{minWidth: 157}}
							disabled={isConnected === false || isAnalyzerConnected === false}
						>
							Upload File{/*
							*/}<input
								type="file"
								hidden
								accept=".xfw"
								onChange={handleFileChange}
							/>
						</PrimaryButton>

						<PrimaryButton
							startIcon={<CloseIcon/>}
							disabled={firmwareFile === null}
							addStyle="!ml-[40px]"
							onClick={clearFile}
						>
							Clear Upload Selection
						</PrimaryButton>



						<Typography variant="body2" 
							style={{ marginTop: 10, marginBottom: 10,
							color: firmwareFile ? "inherit" : "#888", }}>
							{ (firmwareFile && firmwareFile instanceof File) ? `${firmwareFile.name}` : "No file selected" }
						</Typography>
						

						<PrimaryButton
							disabled={ !isUpToDate || firmwareFile === null || !isAnalyzerConnected}
							onClick={installBootloader}>
							Update Firmware
						</PrimaryButton>
					</div>
				</div>
				)}
			</div>
			}
			

            <FirmwareSpinner 
				open={isSpinnerOpen}
				onClose={closeSpinner} 
				onConfirm={closeSpinner}
				percent={percent}
			/>

			{isImportOpen && (
				<ImportModal
					onClose={closeImportModal}
				/>
			)}
		</div>
	);
};

export default DeviceWarranty;